import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../common/Modal/Modal";
import { CardSelected } from "../../../type";
import { lettersToNumber, numberToLetters } from "../../../utils";
import toast from "react-hot-toast";
import {
  LabelStyled,
  PrimaryButton,
  StyledInput,
} from "../../common/common.style";
import dayjs from "dayjs";
import { FlexBox } from "../../primitives";

interface ModalAddPurchasePriceProps {
  setOpenModalAddPurchasePrice: (arg1: boolean) => void;
  cardsBoard: CardSelected[];
  setCardsBoard: (arg1: CardSelected[]) => void;
}

export const ModalAddPurchasePrice = ({
  setOpenModalAddPurchasePrice,
  cardsBoard,
  setCardsBoard,
}: ModalAddPurchasePriceProps) => {
  const [purchasePrice, setPurchasePrice] = useState(
    cardsBoard.reduce((sum, card) => sum + card.quantity * card.price, 0)
  );

  const mergeCards = (cards: CardSelected[]) => {
    const merged = new Map<string, CardSelected>();

    for (const card of cards) {
      const key = JSON.stringify({
        blue_print_id: card.id,
        condition: card?.condition,
        is_foil: card.finish === "foil" || false,
        description: card?.description || "",
        language: card.language,
        is_signed: card?.signed || false,
        is_first_edition: card?.isFirstEdition || false,
        is_reverse: card?.isReversed || false,
        is_altered: false,
      });

      const existing = merged.get(key);

      if (existing) {
        existing.quantity += card.quantity;
      } else {
        merged.set(key, card);
      }
    }

    return Array.from(merged.values());
  };

  const createCommentsForCardsHandler = useCallback(() => {
    const paid = purchasePrice;

    const totalCardsPrice = cardsBoard.reduce(
      (sum, card) => sum + card.quantity * card.price,
      0
    );

    let price = 0;
    let priceLetter = 0;

    const today = dayjs().format("MMM/YY");

    const cardWithComments = cardsBoard.map((card) => {
      let newCardPrice = parseFloat(
        ((paid / totalCardsPrice) * card.price).toFixed(2)
      );

      newCardPrice = newCardPrice || 0.01;

      const letterToAdd = numberToLetters(parseFloat(newCardPrice.toFixed(2)));

      price += newCardPrice * card.quantity;
      priceLetter += parseFloat(lettersToNumber(letterToAdd)) * card.quantity;

      return {
        ...card,
        price: newCardPrice,
        description: `BEST PRICE! SAFE AND QUALITY GRADING! FAST SHIPPING! ${letterToAdd}-${today}`,
      };
    });

    setCardsBoard(mergeCards(cardWithComments));

    toast.success(
      `price changed successfully ${price?.toFixed(
        2
      )}, price letters ${priceLetter?.toFixed(2)}`,
      {
        id: "price-letter",
        duration: 5000,
      }
    );

    setOpenModalAddPurchasePrice(false);
  }, [cardsBoard, purchasePrice, setCardsBoard, setOpenModalAddPurchasePrice]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenModalAddPurchasePrice(false);
      } else if (e.key === "Enter") {
        createCommentsForCardsHandler();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [createCommentsForCardsHandler, setOpenModalAddPurchasePrice]);

  return (
    <Modal onClose={() => setOpenModalAddPurchasePrice(false)}>
      <FlexBox
        css={{
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 20,
          width: "100%",
        }}
      >
        <LabelStyled>SET PURCHASE PRICE</LabelStyled>
        <StyledInput
          type="number"
          min={0}
          value={purchasePrice}
          onChange={(e) => {
            setPurchasePrice(+e.target.value);
          }}
          css={{ width: 330, marginTop: 10 }}
          onKeyDown={(e) => {
            if (["e", "E", "+", "-"].includes(e.key)) {
              e.preventDefault();
            }
          }}
        />

        <PrimaryButton onClick={() => createCommentsForCardsHandler()}>
          CONFIRM
        </PrimaryButton>
      </FlexBox>
    </Modal>
  );
};
